import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeProvider';
import { AuthProvider } from './context/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { ToastProvider } from './stores/toastStore';
import { ToastContainer } from './components/ui/atoms/ToastContainer/ToastContainer';
import { ActiveAquariumDataProvider } from './contexts/ActiveAquariumDataProvider';
import { AquariumRouteSync } from './components/routing/AquariumRouteSync';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { IdleSessionGuard } from './components/auth/IdleSessionGuard/IdleSessionGuard';
import { AppRootErrorBoundary, AppRouteErrorBoundary } from './components/errors/AppErrorBoundary';
import { OnboardingProvider } from './components/onboarding/OnboardingContext';
import { FeedbackProvider } from './components/appFeedback/FeedbackContext';
import { FeedbackDialog } from './components/appFeedback/FeedbackDialog';
import { OptInPromptProvider } from './components/optInPrompts/OptInPromptContext';
import { OptInPromptDialog } from './components/optInPrompts/OptInPromptDialog';
import AdminFeedbackPage from './pages/AdminFeedbackPage';
import AdminOptInPromptsPage from './pages/AdminOptInPromptsPage';
import AdminVoiceQualityPage from './pages/AdminVoiceQualityPage';
import { OnboardingTour } from './components/onboarding/OnboardingTour';
import { PrivacyPolicyNotice } from './components/legal/PrivacyPolicyNotice/PrivacyPolicyNotice';
import { TermsOfServiceConsent } from './components/legal/TermsOfServiceConsent/TermsOfServiceConsent';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AccountPendingPage from './pages/AccountPendingPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import AquariumSelectionPage from './pages/AquariumSelectionPage';
import AquariumDetailPage from './pages/AquariumDetailPage';
import IcpAnalysisPage from './pages/IcpAnalysisPage';
import IcpUploadPage from './pages/IcpUploadPage';
import AlertsPage from './pages/AlertsPage';
import SettingsPage from './pages/SettingsPage';
import AdminUserManagementPage from './pages/AdminUserManagement';
import AdminAuditLogPage from './pages/AdminAuditLogPage';
import AdminLicensesPage from './pages/AdminLicensesPage';
import AdminAiUsagePage from './pages/AdminAiUsagePage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminGdprPage from './pages/AdminGdprPage';
import { LicensePage } from './pages/Settings/LicensePage';
import HealthPage from './pages/HealthPage';
import LegalPage from './pages/LegalPage';
import NotFoundPage from './pages/NotFoundPage';
import CreateAquariumPage from './pages/CreateAquariumPage';
import { JournalOverviewPage } from './pages/JournalOverviewPage';
import JournalEntryDetailPage from './pages/JournalEntryDetailPage';
import { ManualMeasurementEntryPage } from './pages/ManualMeasurementEntryPage';
import { GalleryPage } from './pages/GalleryPage';
import { CoralCheckPage } from './pages/CoralCheckPage';
import { MentorPage } from './pages/MentorPage';
import { IntegrationsPage } from './pages/Integrations/IntegrationsPage';
import { LivestockPage } from './pages/LivestockPage';
import { LivestockOverviewPage } from './pages/LivestockOverviewPage';
import { AquariumTimelinePage } from './pages/AquariumTimelinePage';
import { IterationHistoryPage } from './pages/zeovit/IterationHistoryPage';
import { DosingPage } from './pages/DosingPage';
import { ParameterTargetsPage } from './pages/ParameterTargetsPage';
import { EquipmentSafetyPage } from './pages/EquipmentSafetyPage';
import { IncidentsPage } from './pages/IncidentsPage';
import { BudgetPage } from './pages/BudgetPage';
import { IncidentDetailPage } from './pages/IncidentDetailPage';
import { QuarantinePage } from './pages/QuarantinePage';
import { LightingPage } from './pages/LightingPage';
import { LightSurveyPage } from './pages/LightSurveyPage';
import { FlowPage } from './pages/FlowPage';
import { FlowSurveyPage } from './pages/FlowSurveyPage';
import { AquariumComparisonPage } from './pages/AquariumComparisonPage';
import { CameraSourcesPage } from './pages/Settings/CameraSourcesPage';
import { CommunityPage } from './pages/CommunityPage';
import { CommunityEntryPage } from './pages/CommunityEntryPage';
import { SharedImagePage } from './pages/SharedImagePage';
import { EmailConfirmPage, EmailUnsubscribePage } from './pages/EmailLinkPage';
import AdminReleaseNotesPage from './pages/AdminReleaseNotesPage';
import { ZeovitNewRecommendationPage } from './pages/ZeovitNewRecommendationPage';
import { PeriodicCheckPage } from './pages/zeovit/PeriodicCheckPage';
import { CheckResultPage } from './pages/zeovit/CheckResultPage';
import { RecommendationDetailPage } from './pages/zeovit/RecommendationDetailPage';
import { TestSessionPage } from './pages/TestSessionPage';
import { GuidedTestSessionPage } from './pages/GuidedTestSessionPage';
import { EmergencyPage } from './pages/EmergencyPage';
import { CaptureReviewPage } from './pages/CaptureReviewPage';
import { CaptureProvider } from './components/capture/CaptureContext';
import { VoiceJournalRoute } from './components/capture/VoiceJournalRoute';

function App() {
  return (
    <AppRootErrorBoundary>
    <ThemeProvider>
      <ToastProvider>
      <ErrorProvider>
        <AuthProvider>
          <ActiveAquariumDataProvider>
          {/* Priority notices — other auto-opening dialogs (onboarding tour, news)
              wait while they are checked or open: first the privacy policy update
              notice, then the terms of service re-acceptance (one after the other). */}
          <PrivacyPolicyNotice>
          <TermsOfServiceConsent>
          <BrowserRouter>
            <AquariumRouteSync />
            {/* Automatic sign-out after inactivity, with a warning first (lib/idleSession). */}
            <IdleSessionGuard />
            {/* Onboarding tour: opened by AppLayout (once per session) or by hand. */}
            <OnboardingProvider>
            <OptInPromptProvider>
            <FeedbackProvider>
            {/* Voice notes: the recorder lives above the routes so a recording survives navigation. */}
            <CaptureProvider>
            <AppRouteErrorBoundary>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Closed beta: account waits for (or was refused) an admin's approval */}
              <Route path="/account-pending" element={<AccountPendingPage />} />
              <Route path="/health" element={<HealthPage />} />
              {/* Public legal pages (no login) — German aliases share the same page */}
              <Route path="/privacy" element={<LegalPage document="privacy-policy" />} />
              <Route path="/datenschutz" element={<LegalPage document="privacy-policy" />} />
              <Route path="/terms" element={<LegalPage document="terms-of-service" />} />
              <Route path="/agb" element={<LegalPage document="terms-of-service" />} />
              <Route path="/imprint" element={<LegalPage document="imprint" />} />
              <Route path="/impressum" element={<LegalPage document="imprint" />} />
              {/* Public landing pages of the links in our e-mails (signed token, no login) */}
              <Route path="/email/confirm" element={<EmailConfirmPage />} />
              <Route path="/email/unsubscribe" element={<EmailUnsubscribePage />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums"
                element={
                  <ProtectedRoute>
                    <AquariumSelectionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/new"
                element={
                  <ProtectedRoute>
                    <CreateAquariumPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/icp-analysis"
                element={
                  <ProtectedRoute>
                    <IcpAnalysisPage />
                  </ProtectedRoute>
                }
              />
              {/* Voice journal: "To review" inbox of all captures across tanks (docs/voiceFeature.md). */}
              <Route
                path="/review"
                element={
                  <ProtectedRoute>
                    <CaptureReviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alerts"
                element={
                  <ProtectedRoute>
                    <AlertsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/camera-sources"
                element={
                  <ProtectedRoute>
                    <CameraSourcesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/license"
                element={
                  <ProtectedRoute>
                    <LicensePage />
                  </ProtectedRoute>
                }
              />
              {/* Admin area: one route per tab; pages show a notice to non-admins (API enforces RBAC). */}
              <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <AdminUserManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/licenses"
                element={
                  <ProtectedRoute>
                    <AdminLicensesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/ai-usage"
                element={
                  <ProtectedRoute>
                    <AdminAiUsagePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute>
                    <AdminAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <AnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/gdpr"
                element={
                  <ProtectedRoute>
                    <AdminGdprPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/release-notes"
                element={
                  <ProtectedRoute>
                    <AdminReleaseNotesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/feedback"
                element={
                  <ProtectedRoute>
                    <AdminFeedbackPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/opt-ins"
                element={
                  <ProtectedRoute>
                    <AdminOptInPromptsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/voice-quality"
                element={
                  <ProtectedRoute>
                    <AdminVoiceQualityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audit-log"
                element={
                  <ProtectedRoute>
                    <AdminAuditLogPage />
                  </ProtectedRoute>
                }
              />

              {/* Community: public journal entries, likes, comments (audit #11) */}
              <Route
                path="/community"
                element={
                  <ProtectedRoute>
                    <CommunityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/community/:entryId"
                element={
                  <ProtectedRoute>
                    <CommunityEntryPage />
                  </ProtectedRoute>
                }
              />
              {/* Target of image share links (GET /api/v1/images/:imageId/share); sign-in required. */}
              <Route
                path="/images/:imageId"
                element={
                  <ProtectedRoute>
                    <SharedImagePage />
                  </ProtectedRoute>
                }
              />

              {/* Default redirect */}
              {/* Aquarium-scoped routes */}
              <Route
                path="/aquariums/:aquariumId"
                element={<Navigate to="timeline" replace />}
              />
              <Route
                path="/aquariums/:aquariumId/tasks"
                element={
                  <ProtectedRoute>
                    <AquariumDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/icp-analyses"
                element={
                  <ProtectedRoute>
                    <IcpAnalysisPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/journal"
                element={
                  <ProtectedRoute>
                    <JournalOverviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/journal/:entryId"
                element={
                  <ProtectedRoute>
                    <JournalEntryDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/gallery"
                element={
                  <ProtectedRoute>
                    <GalleryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/coral-check"
                element={
                  <ProtectedRoute>
                    <CoralCheckPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/mentor"
                element={
                  <ProtectedRoute>
                    <MentorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/measurements"
                element={
                  <ProtectedRoute>
                    <ManualMeasurementEntryPage defaultView="values" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/measurements/new"
                element={
                  <ProtectedRoute>
                    <ManualMeasurementEntryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/measurements/session"
                element={
                  <ProtectedRoute>
                    <TestSessionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/measurements/session/guided"
                element={
                  <ProtectedRoute>
                    <VoiceJournalRoute fallback="measurements/session">
                      <GuidedTestSessionPage />
                    </VoiceJournalRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/emergency"
                element={
                  <ProtectedRoute>
                    <VoiceJournalRoute fallback="incidents">
                      <EmergencyPage />
                    </VoiceJournalRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/icp/upload"
                element={
                  <ProtectedRoute>
                    <IcpUploadPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/integrations"
                element={<ProtectedRoute />}
              >
                <Route index element={<Navigate to="controller" replace />} />
                <Route path="controller" element={<IntegrationsPage />} />
                <Route path="camera" element={<IntegrationsPage />} />
                <Route path="focustronic" element={<IntegrationsPage />} />
                <Route path="reefbot" element={<IntegrationsPage />} />
                <Route path="fauna-marin" element={<IntegrationsPage />} />
              </Route>
              <Route
                path="/aquariums/:aquariumId/livestock"
                element={
                  <ProtectedRoute>
                    <LivestockPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/livestock/overview"
                element={
                  <ProtectedRoute>
                    <LivestockOverviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/livestock/overview/:analysisId"
                element={
                  <ProtectedRoute>
                    <LivestockOverviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/timeline"
                element={
                  <ProtectedRoute>
                    <AquariumTimelinePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/dosing"
                element={
                  <ProtectedRoute>
                    <DosingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/parameter-targets"
                element={
                  <ProtectedRoute>
                    <ParameterTargetsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/equipment-safety"
                element={
                  <ProtectedRoute>
                    <EquipmentSafetyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/budget"
                element={
                  <ProtectedRoute>
                    <BudgetPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/incidents"
                element={
                  <ProtectedRoute>
                    <IncidentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/lighting"
                element={
                  <ProtectedRoute>
                    <LightingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/lighting/:surveyId"
                element={
                  <ProtectedRoute>
                    <LightSurveyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/flow"
                element={
                  <ProtectedRoute>
                    <FlowPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/flow/:surveyId"
                element={
                  <ProtectedRoute>
                    <FlowSurveyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/quarantine"
                element={
                  <ProtectedRoute>
                    <QuarantinePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/incidents/:incidentId"
                element={
                  <ProtectedRoute>
                    <IncidentDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/comparisons"
                element={
                  <ProtectedRoute>
                    <AquariumComparisonPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/zeovit-recommendations/new"
                element={
                  <ProtectedRoute>
                    <ZeovitNewRecommendationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/zeovit-recommendations"
                element={
                  <ProtectedRoute>
                    <IterationHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/zeovit-recommendations/:recommendationId"
                element={
                  <ProtectedRoute>
                    <RecommendationDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/zeovit-recommendations/:recommendationId/check"
                element={
                  <ProtectedRoute>
                    <PeriodicCheckPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aquariums/:aquariumId/zeovit-recommendations/:recommendationId/check-result"
                element={
                  <ProtectedRoute>
                    <CheckResultPage />
                  </ProtectedRoute>
                }
              />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </AppRouteErrorBoundary>
            </CaptureProvider>
            <OnboardingTour />
            <FeedbackDialog />
            <OptInPromptDialog />
            </FeedbackProvider>
            </OptInPromptProvider>
            </OnboardingProvider>
          </BrowserRouter>
          </TermsOfServiceConsent>
          </PrivacyPolicyNotice>
          </ActiveAquariumDataProvider>
        </AuthProvider>
      </ErrorProvider>
      <ToastContainer />
      </ToastProvider>
    </ThemeProvider>
    </AppRootErrorBoundary>
  );
}

export default App;
